import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faCalendarAlt } from '@fortawesome/free-solid-svg-icons';
import { Button, ButtonGroup, Typography } from '@material-ui/core';
import moment from 'moment';
import { makeStyles } from '@material-ui/core/styles';
import TooltipDateTimeRangePicker from './TooltipDateTimeRangePicker';
import { DateTimeRange } from './DateTimeRangePicker';

const useStyles = makeStyles((theme) => ({
  timeDiv: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    '& > div': {
      margin: theme.spacing(1),
    },
  },
}));

export interface TimePickerButton {
  variant: 'text' | 'outlined' | 'contained';
  color: 'inherit' | 'primary' | 'secondary' | 'default';
  duration: moment.Duration;
  display: string;
  hourlyTimeRange: number;
}

const defaultDateTimeRange: DateTimeRange = {
  start: moment().startOf('minute').subtract(1, 'h'),
  end: moment().startOf('minute'),
};

interface CustomAndRelativeDateTimeRangePickerProps {
  updateTimeRange?(dateTimeRange: DateTimeRange): void;
  setIsRelative?(updated: boolean): void;
  isRelative?: boolean;
  initialTimeRange?: DateTimeRange;
  timePickerButtons?: TimePickerButton[];
  rangePickerName?: string;
}

const CustomAndRelativeDateTimeRangePicker: React.FC<CustomAndRelativeDateTimeRangePickerProps> = ({
  updateTimeRange = () => {},
  setIsRelative = () => {},
  isRelative = true,
  initialTimeRange = defaultDateTimeRange,
  timePickerButtons = [],
  rangePickerName = 'Custom',
}: CustomAndRelativeDateTimeRangePickerProps) => {
  const [timeRange, setTimeRange] = useState<DateTimeRange>();
  const [relativePicker, setRelativePicker] = useState<boolean>();
  const [relativeDatePickerButtons, setRelativeDatePickerButtons] = useState<TimePickerButton[]>(timePickerButtons);

  const timeRangeHandler = (range: DateTimeRange) => {
    setTimeRange(range);
    updateTimeRange(range);
  };

  const handleRelativeTimeChange = (index: number, duration: moment.Duration) => {
    const buttons = relativeDatePickerButtons.map((button, i) => {
      return i === index
        ? ({ ...button, variant: 'contained', color: 'primary' } as TimePickerButton)
        : ({ ...button, variant: 'outlined', color: 'default' } as TimePickerButton);
    });
    setRelativeDatePickerButtons(buttons);
    setRelativePicker(true);

    const now = moment().startOf('minute');
    timeRangeHandler({
      start: now.clone().subtract(duration),
      end: now.clone(),
    });
    setIsRelative(true);
  };

  const resetRelativeDatePickerButtons = () => {
    let buttons = relativeDatePickerButtons;
    buttons = buttons.map((button) => {
      return { ...button, variant: 'outlined', color: 'default' };
    });
    setRelativeDatePickerButtons(buttons);
  };

  const updateOnCustomDatePickerDismiss = (updated: boolean) => {
    if (updated) {
      setIsRelative(false);
      resetRelativeDatePickerButtons();
    }
  };

  useEffect(() => {
    setRelativePicker(isRelative);
  }, [isRelative]);

  useEffect(() => {
    setTimeRange(initialTimeRange);
  }, [initialTimeRange]);

  useEffect(() => {
    if (typeof timeRange !== 'undefined' && typeof relativePicker !== 'undefined') {
      const updatedHourlyTimeRange = moment(timeRange.end).diff(moment(timeRange.start), 'h');
      setRelativeDatePickerButtons((buttons) =>
        buttons.map((button) =>
          relativePicker && button.hourlyTimeRange === updatedHourlyTimeRange
            ? { ...button, variant: 'contained', color: 'primary' }
            : { ...button, variant: 'outlined', color: 'default' }
        )
      );
    }
  }, [timeRange, relativePicker]);

  const classes = useStyles();

  return typeof timeRange === 'undefined' || typeof relativePicker === 'undefined' ? (
    <div />
  ) : (
    <div className={classes.timeDiv}>
      <div>
        <Typography color="textSecondary" variant="body2">
          {moment(timeRange.start).format('MMM D, hh:mm a')} <FontAwesomeIcon icon={faArrowRight} fixedWidth />{' '}
          {moment(timeRange.end).format('MMM D, hh:mm a')}
        </Typography>
      </div>
      <div>
        <ButtonGroup>
          {relativeDatePickerButtons.map((button, index) => (
            <Button
              key={button.duration.toISOString()}
              variant={button.variant}
              color={button.color}
              onClick={() => {
                handleRelativeTimeChange(index, button.duration);
              }}
            >
              {`${button.display}`}
            </Button>
          ))}
          <TooltipDateTimeRangePicker
            updateDateTimeRange={timeRangeHandler}
            setUpdated={updateOnCustomDatePickerDismiss}
            initialDateTimeRange={timeRange}
            isActive={!relativePicker}
            CustomComponent={() => (
              <span>
                {rangePickerName}
                <FontAwesomeIcon
                  style={{
                    marginLeft: '5px',
                  }}
                  fixedWidth
                  icon={faCalendarAlt}
                />
              </span>
            )}
          />
        </ButtonGroup>
      </div>
    </div>
  );
};

export default CustomAndRelativeDateTimeRangePicker;
